/**
 * Formulaires – portage de design/components/bundle.js (TextField, Toggle, SegmentedControl)
 * + ajouts AGORA absents du bundle : TextArea, SelectField (même structure `ag-field`, styles agx- dans core/app.css) ;
 * la liste avec recherche (Combobox, Downshift) est dans liste-recherche.ts.
 * Comportement délégué à des moteurs éprouvés, sans changer le DOM ni les classes du design : Toggle = Radix Switch,
 * SegmentedControl = Radix RadioGroup (flèches, Début / Fin, un seul arrêt de tabulation).
 */
import React from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import * as Switch from '@radix-ui/react-switch';
import { cx, h, Icon, useIdStable } from './base';
import { useSelectionSuitFocus } from './liste-choix';
import { usePastilleGlissante } from './pastille';

export interface TextFieldProps {
  type?: string; autoComplete?: string; label: string; value?: string; placeholder?: string; help?: string; error?: string;
  id?: string; inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  /** Ajouts AGORA (champ contrôlé) : sans onChange, le champ reste non contrôlé (defaultValue) comme la référence. */
  onChange?: (valeur: string) => void; name?: string; required?: boolean; maxLength?: number; autoFocus?: boolean;
  disabled?: boolean; pattern?: string;
  /** Ajout AGORA : champ compact (hauteur --control-md), pour les barres d'outils denses (recherche d'une liste). */
  compact?: boolean;
  /**
   * Ajout AGORA : secret saisi pour être rangé ailleurs (Trousseau) – le navigateur ne doit ni proposer de l'enregistrer
   * dans son gestionnaire de mots de passe, ni y remplir ceux de l'utilisateur. `masque` (points) ou `clair` ; remplace
   * `type` et `autoComplete`. Voir attributsSecret.
   */
  secret?: 'masque' | 'clair';
}

/** -webkit-text-security (Chromium, WebKit) : masque un champ texte sans en faire un champ mot de passe. */
const MASQUE_CSS = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('-webkit-text-security', 'disc');

/**
 * Champ texte, jamais type="password" : Edge et Chrome proposent d'enregistrer tout champ mot de passe envoyé (et
 * retiennent un champ qui l'a été, même repassé en texte). Masqué par -webkit-text-security ; repli type="password"
 * seulement là où ce masquage n'existe pas. Attributs data-* : extensions (1Password, LastPass, Bitwarden, Dashlane).
 * Masqué, il ne se copie ni ne se coupe, comme un champ mot de passe. Compromis (docs/TROUSSEAU.md) : un lecteur
 * d'écran le lit comme un texte ; Firefox (repli) peut encore proposer de l'enregistrer.
 */
function attributsSecret(secret: 'masque' | 'clair') {
  const masque = secret === 'masque';
  const bloquer = masque ? (e: React.ClipboardEvent) => e.preventDefault() : undefined;
  return {
    type: masque && !MASQUE_CSS ? 'password' : 'text', className: masque && MASQUE_CSS ? 'agx-champ-masque' : undefined,
    autoComplete: 'off', spellCheck: false, autoCapitalize: 'off', autoCorrect: 'off', onCopy: bloquer, onCut: bloquer,
    'data-1p-ignore': 'true', 'data-lpignore': 'true', 'data-bwignore': 'true', 'data-form-type': 'other',
  };
}

export function TextField(p: TextFieldProps) {
  const id = useIdStable('ag-f', p.id);
  const controle = p.onChange ? { value: p.value ?? '', onChange: (e: React.ChangeEvent<HTMLInputElement>) => p.onChange!(e.target.value) } : { defaultValue: p.value };
  return h('div', { className: cx('ag-field', p.error && 'ag-field-error', p.compact && 'agx-champ-compact') },
    h('label', { htmlFor: id }, p.label),
    h('input', {
      id, ...controle, placeholder: p.placeholder, type: p.type, autoComplete: p.autoComplete, 'aria-invalid': !!p.error,
      'aria-describedby': p.error || p.help ? id + '-h' : undefined, inputMode: p.inputMode, name: p.name, required: p.required,
      maxLength: p.maxLength, autoFocus: p.autoFocus, disabled: p.disabled, pattern: p.pattern,
      ...(p.secret ? attributsSecret(p.secret) : {}),
    }),
    p.error || p.help ? h('span', { id: id + '-h', className: 'ag-field-help' }, p.error || p.help) : null);
}

/**
 * Ajout AGORA – zone de texte multiligne (ex. description d'un incident) : mêmes libellé, aide, erreur et états que
 * TextField (`ag-field`, `ag-field-error`, `ag-field-help`). Hauteur ajustable verticalement ; occupe toute la largeur
 * de sa cellule (`agx-champ-large` via `className` pour toute la ligne d'une grille `agx-champs`).
 * Tous les textes (libellé, aide, erreur, compteur) sont fournis déjà traduits par l'appelant.
 */
export interface TextAreaProps {
  label: string; value?: string; placeholder?: string; help?: string; error?: string; id?: string;
  /** Sans onChange, le champ reste non contrôlé (defaultValue), comme TextField. */
  onChange?: (valeur: string) => void; name?: string; required?: boolean; maxLength?: number; autoFocus?: boolean;
  disabled?: boolean;
  /** Lignes visibles à l'ouverture (4 par défaut). */
  rows?: number;
  /** Compteur facultatif, affiché seulement avec maxLength : texte traduit, ex. t('…', { n, max }) → « 120 / 2 000 ». */
  counterLabel?: (longueur: number, max: number) => string;
  className?: string;
}

export function TextArea(p: TextAreaProps) {
  const id = useIdStable('ag-f', p.id);
  const [saisie, setSaisie] = React.useState((p.value ?? '').length);
  const longueur = p.onChange ? (p.value ?? '').length : saisie;
  const controle = p.onChange
    ? { value: p.value ?? '', onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => p.onChange!(e.target.value) }
    : { defaultValue: p.value, onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => setSaisie(e.target.value.length) };
  const aide = p.error || p.help;
  const compteur = p.maxLength && p.counterLabel ? p.counterLabel(longueur, p.maxLength) : null;
  const decrit = [aide && id + '-h', compteur && id + '-c'].filter(Boolean).join(' ') || undefined;
  return h('div', { className: cx('ag-field', 'agx-textarea', p.error && 'ag-field-error', p.className) },
    h('label', { htmlFor: id }, p.label),
    h('textarea', {
      id, ...controle, placeholder: p.placeholder, rows: p.rows ?? 4, 'aria-invalid': !!p.error, 'aria-describedby': decrit,
      name: p.name, required: p.required, maxLength: p.maxLength, autoFocus: p.autoFocus, disabled: p.disabled,
    }),
    aide || compteur ? h('div', { className: 'agx-champ-pied' },
      aide ? h('span', { id: id + '-h', className: 'ag-field-help' }, aide) : null,
      compteur ? h('span', { id: id + '-c', className: 'ag-field-help agx-compteur' }, compteur) : null) : null);
}

export interface SelectOption { value: string; label: string; disabled?: boolean }

/**
 * Ajout AGORA – liste déroulante (ex. catégorie d'un incident) sur l'élément natif `select` : clavier, lecteurs
 * d'écran et sélecteur mobile natifs, habillage du design (mêmes libellé, aide, erreur et états que TextField).
 * `videLabel` ajoute en tête une option vide (valeur '') – texte fourni traduit par l'appelant, ex. « Choisir… »,
 * « Non renseigné » ou « Tous » pour un filtre ; le fournir dès que la valeur peut être vide. Avec `required`, l'option
 * vide ne satisfait pas la validation native.
 */
export interface SelectFieldProps {
  label: string; options: SelectOption[]; value?: string; videLabel?: string; help?: string; error?: string; id?: string;
  /** Sans onChange, le champ reste non contrôlé (defaultValue), comme TextField. */
  onChange?: (valeur: string) => void; name?: string; required?: boolean; disabled?: boolean; autoFocus?: boolean;
  className?: string;
  /** Champ compact (hauteur --control-md), comme TextField. */
  compact?: boolean;
}

export function SelectField(p: SelectFieldProps) {
  const id = useIdStable('ag-f', p.id);
  const controle = p.onChange
    ? { value: p.value ?? '', onChange: (e: React.ChangeEvent<HTMLSelectElement>) => p.onChange!(e.target.value) }
    : { defaultValue: p.value ?? '' };
  const aide = p.error || p.help;
  return h('div', { className: cx('ag-field', p.error && 'ag-field-error', p.compact && 'agx-champ-compact', p.className) },
    h('label', { htmlFor: id }, p.label),
    h('span', { className: 'agx-select' },
      h('select', {
        id, ...controle, 'aria-invalid': !!p.error, 'aria-describedby': aide ? id + '-h' : undefined,
        name: p.name, required: p.required, disabled: p.disabled, autoFocus: p.autoFocus,
      },
        p.videLabel !== undefined ? h('option', { value: '' }, p.videLabel) : null,
        (p.options || []).map((o) => h('option', { key: o.value, value: o.value, disabled: o.disabled }, o.label))),
      h(Icon, { name: 'chevron', size: 16, className: 'agx-select-chevron' })),
    aide ? h('span', { id: id + '-h', className: 'ag-field-help' }, aide) : null);
}

export interface ToggleProps { label: string; help?: string; checked?: boolean; onChange?: (v: boolean) => void; card?: boolean; disabled?: boolean; id?: string }

export function Toggle(p: ToggleProps) {
  const id = useIdStable('ag-t', p.id);
  const [interne, setInterne] = React.useState(!!p.checked);
  const on = p.onChange ? !!p.checked : interne;
  // Radix Switch : même DOM que la référence (button.ag-switch[role=switch][aria-checked] > span) ; dans un formulaire,
  // il ajoute une case à cocher cachée pour l'envoi natif.
  return h('div', { className: cx('ag-toggle-row', p.card && 'ag-glass-card') },
    h('div', { className: 'ag-toggle-text' }, h('label', { htmlFor: id }, p.label), p.help ? h('small', null, p.help) : null),
    h(Switch.Root, {
      id, checked: on, disabled: p.disabled, className: 'ag-switch',
      onCheckedChange: (v: boolean) => (p.onChange ? p.onChange(v) : setInterne(v)),
    }, h(Switch.Thumb)));
}

export interface SegmentedOption { value: string; label: string; title?: string; lang?: string }
export interface SegmentedControlProps {
  options: SegmentedOption[]; value: string; onChange?: (v: string) => void; ariaLabel?: string;
  /** Ajout AGORA : options compactes (hauteur --control-sm), chacune à la largeur de son libellé. */
  compact?: boolean;
}

/**
 * Radix RadioGroup : un seul arrêt de tabulation (l'option choisie), ← → ↑ ↓ pour changer d'option (en boucle), Début /
 * Fin pour la première / dernière. **La sélection suit le focus** quelle que soit la touche (useSelectionSuitFocus,
 * comme ChoiceList) : Radix seul ne cochait pas l'option atteinte par Début / Fin ni par une frappe très brève.
 * Mêmes classes que la référence (`ag-seg-on`). Pastille glissante d'une option à l'autre (Motion, décorative) :
 * pastille.ts.
 */
export function SegmentedControl(p: SegmentedControlProps) {
  const pastille = usePastilleGlissante();
  const { emettre, surFocus } = useSelectionSuitFocus(p.value, p.onChange);
  return h(RadioGroup.Root, {
    ref: pastille.ref,
    className: cx('ag-seg', 'ag-glass-card', p.compact && 'agx-seg-compact'), 'aria-label': p.ariaLabel, value: p.value, loop: true, onValueChange: emettre,
  },
  pastille.element,
  (p.options || []).map((o) => h(RadioGroup.Item, {
    key: o.value, value: o.value, title: o.title, lang: o.lang, className: cx('ag-seg-opt', o.value === p.value && 'ag-seg-on'),
    onFocus: surFocus(o.value),
  }, o.label)));
}

